import React, { useMemo } from 'react';
import {
  BusinessTitlesUtil,
  CondensedHeader,
  CondensedLarge,
  Divider,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  SectionHeader,
  TaxonomyUtil,
  Well,
} from '@axiom/ui';
import { BarredLocationsConst, CountryCodes, Education } from '@axiom/const';
import type {
  Candidate,
  Taxonomy,
  AfcCandidateSearch,
  LimitedCandidate,
  SavedCandidate,
} from '@axiom/validation';

import { BarredLocationsUtil } from '../../utils/barred-locations-util';
import { CandidateUtil } from '../../utils/candidate-util';

type CandidateType =
  | Candidate
  | LimitedCandidate
  | AfcCandidateSearch
  | SavedCandidate['candidate'];

export type TalentHighlightsProps = {
  candidate: CandidateType;
  location: string | null;
  taxonomy: Taxonomy;
};

export const TalentHighlights = ({
  candidate,
  location,
  taxonomy,
}: TalentHighlightsProps) => {
  const formattedIndustries = useMemo(
    () => CandidateUtil.formatIndustries(candidate.industries),
    [candidate.industries]
  );

  const yearsOfExperienceRange = useMemo(
    () => CandidateUtil.getYearsOfExperienceRange(candidate.yearsOfExperience),
    [candidate.yearsOfExperience]
  );

  const practiceAreaFocusArea = useMemo(
    () =>
      TaxonomyUtil.formatPracticeAreaFocusAreaForDisplay(
        taxonomy,
        candidate.primaryPracticeArea,
        candidate.primaryFocusArea
      ),
    [taxonomy, candidate.primaryPracticeArea, candidate.primaryFocusArea]
  );

  const formattedLawFirms = useMemo(
    () => CandidateUtil.getFormattedLawFirms(candidate),
    [candidate]
  );

  const legalRole = useMemo(
    () =>
      BusinessTitlesUtil.locationConversion({
        businessTitle:
          TaxonomyUtil.getBusinessTitle(taxonomy, candidate.businessTitle)
            ?.displayName ?? null,
        candidate,
      }),
    [taxonomy, candidate]
  );

  const barredLocationNames = useMemo(
    () =>
      (candidate.barredLocations ?? [])
        .filter(d => d.name !== BarredLocationsConst.Names.NotAdmitted)
        .map(d => BarredLocationsUtil.replaceCanadian(d.name)),
    [candidate.barredLocations]
  );

  const lawDegreeSchools = useMemo(() => {
    if ('lawDegreeSchools' in candidate && candidate.lawDegreeSchools) {
      return CandidateUtil.getUniqueLawDegreeSchools(
        candidate.lawDegreeSchools
      );
    }
    if ('degrees' in candidate && candidate.degrees) {
      const lawDegreeSchoolsFromDegrees = candidate.degrees.filter(
        item =>
          item.degree &&
          Object.keys(Education.LAW_DEGREES).includes(item.degree)
      );
      return CandidateUtil.getUniqueLawDegreeSchools(
        lawDegreeSchoolsFromDegrees
      );
    }
    return [];
  }, [candidate]);

  const hasHighlights =
    formattedIndustries.length > 0 ||
    !!practiceAreaFocusArea ||
    yearsOfExperienceRange !== '--' ||
    lawDegreeSchools.length > 0 ||
    !!location ||
    barredLocationNames.length > 0 ||
    !!legalRole;

  if (!hasHighlights) {
    return null;
  }

  return (
    <Well name="TALENT_HIGHLIGHTS" background="light blue">
      <SectionHeader>Highlights</SectionHeader>
      <Gutter bottom="16px" />
      <Grid>
        <GridRow>
          {!!practiceAreaFocusArea && (
            <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
              <Gutter bottom="16px">
                <Grid>
                  <GridRow>
                    <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                      <CondensedHeader>Practice area</CondensedHeader>
                    </GridColumn>
                    <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
                      <CondensedLarge name="TALENT_HIGHLIGHTS_PRACTICE_AREA">
                        {practiceAreaFocusArea}
                      </CondensedLarge>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </Gutter>
            </GridColumn>
          )}
          {!!legalRole && (
            <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
              <Gutter bottom="16px">
                <Grid>
                  <GridRow>
                    <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                      <CondensedHeader>Legal role</CondensedHeader>
                    </GridColumn>
                    <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
                      <CondensedLarge name="TALENT_HIGHLIGHTS_LEGAL_ROLE">
                        {legalRole}
                      </CondensedLarge>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </Gutter>
            </GridColumn>
          )}
          {barredLocationNames.length > 0 &&
          candidate.homeOffice?.name !== CountryCodes.HK &&
          candidate.homeOffice?.name !== CountryCodes.SG &&
          TaxonomyUtil.isLawyer(candidate.businessTitle) ? (
            <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
              <Gutter bottom="16px">
                <Grid>
                  <GridRow>
                    <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                      <CondensedHeader>Admissions</CondensedHeader>
                    </GridColumn>

                    <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
                      <CondensedLarge name="TALENT_HIGHLIGHTS_BARRED_LOCATIONS">
                        <Divider>{barredLocationNames}</Divider>
                      </CondensedLarge>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </Gutter>
            </GridColumn>
          ) : null}

          {yearsOfExperienceRange !== '--' && (
            <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
              <Gutter bottom="16px">
                <Grid>
                  <GridRow>
                    <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                      <CondensedHeader>Experience</CondensedHeader>
                    </GridColumn>
                    <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
                      <CondensedLarge name="TALENT_HIGHLIGHTS_YEARS_OF_EXPERIENCE">
                        {yearsOfExperienceRange} years
                      </CondensedLarge>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </Gutter>
            </GridColumn>
          )}
        </GridRow>
        {formattedIndustries.length > 0 && (
          <GridRow gutterBottom="16px">
            <GridColumn largeScreenWidth={2} smallScreenWidth={12}>
              <CondensedHeader>Industries</CondensedHeader>
            </GridColumn>
            <GridColumn largeScreenWidth={10} smallScreenWidth={12}>
              <CondensedLarge name="TALENT_HIGHLIGHTS_INDUSTRIES">
                {formattedIndustries
                  .map(
                    ({ industryValue, yearsOfExperience }) =>
                      `${taxonomy.industries[industryValue]?.displayValue} (${CandidateUtil.formatYOE(`${yearsOfExperience}`)})`
                  )
                  .join(', ')}
              </CondensedLarge>
            </GridColumn>
          </GridRow>
        )}
        {(formattedLawFirms?.length ?? 0) > 0 && (
          <GridRow gutterBottom="16px">
            <GridColumn largeScreenWidth={2} smallScreenWidth={12}>
              <CondensedHeader>Law firms</CondensedHeader>
            </GridColumn>
            <GridColumn largeScreenWidth={10} smallScreenWidth={12}>
              <CondensedLarge name="TALENT_HIGHLIGHTS_LAW_FIRMS">
                <Divider>
                  {formattedLawFirms?.map(({ client }) => client)}
                </Divider>
              </CondensedLarge>
            </GridColumn>
          </GridRow>
        )}
        {lawDegreeSchools.length > 0 && (
          <GridRow gutterBottom="16px">
            <GridColumn largeScreenWidth={2} smallScreenWidth={12}>
              <CondensedHeader>Education</CondensedHeader>
            </GridColumn>
            <GridColumn largeScreenWidth={10} smallScreenWidth={12}>
              <CondensedLarge name="TALENT_HIGHLIGHTS_LAW_DEGREE_SCHOOLS">
                <Divider>
                  {lawDegreeSchools.map(lawDegreeSchool => lawDegreeSchool)}
                </Divider>
              </CondensedLarge>
            </GridColumn>
          </GridRow>
        )}
        {!!location && (
          <GridRow gutterBottom="16px">
            <GridColumn largeScreenWidth={2} smallScreenWidth={12}>
              <CondensedHeader>Location</CondensedHeader>
            </GridColumn>
            <GridColumn largeScreenWidth={10} smallScreenWidth={12}>
              <CondensedLarge name="TALENT_HIGHLIGHTS_LOCATION">
                {location}
              </CondensedLarge>
            </GridColumn>
          </GridRow>
        )}
      </Grid>
    </Well>
  );
};
